import { useEffect, useMemo, useState } from 'react'

interface Options<IDType> {
  trackChanges?: boolean
  isReactiveInitiallySelectedItems?: boolean
  initiallySelectedItems?: IDType[]
  onItemSelected?: (id: IDType) => void
  onItemUnselected?: (id: IDType) => void
  onSelectAll?: () => void
  onUnselectAll?: () => void
  onClearSelectedItems?: () => void
}

const useSelection = <IDType extends number | string>(allIds: IDType[], options: Options<IDType> = {}) => {
  const { isReactiveInitiallySelectedItems = true } = options
  const allIdsSet = useMemo(() => new Set(allIds), [allIds])
  const [selectedIdsSet, setSelectedIdsSet] = useState<Set<IDType>>(new Set(options.initiallySelectedItems))
  const [changedItems, setChangedItems] = useState<Record<string, boolean>>({})
  const [clearSelectionTrigger, setClearSelectionTrigger] = useState(0)

  useEffect(() => {
    if (isReactiveInitiallySelectedItems) {
      setSelectedIdsSet(new Set(options.initiallySelectedItems))
      setChangedItems({})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReactiveInitiallySelectedItems, options.initiallySelectedItems?.join()])

  useEffect(() => {
    setSelectedIdsSet(new Set(options.initiallySelectedItems))
    setChangedItems({})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearSelectionTrigger])

  const selectAll = () => {
    setSelectedIdsSet(allIdsSet)
    options.onSelectAll?.()
  }

  const toggleSelectAll = () => {
    const isAllSelected = selectedIdsSet.size === allIdsSet.size

    if (isAllSelected) {
      setSelectedIdsSet(new Set([]))
      options.onUnselectAll?.()
    } else {
      setSelectedIdsSet(allIdsSet)
      options.onSelectAll?.()
    }

    if (!options.trackChanges) {
      return
    }

    const changes = allIds.reduce((acc: Record<IDType, boolean>, id) => {
      const isCurrentlySelected = selectedIdsSet.has(id)

      if (isAllSelected && isCurrentlySelected) {
        acc[id] = false
      } else if (!isCurrentlySelected) {
        acc[id] = true
      }

      return acc
    }, {} as Record<IDType, boolean>)

    setChangedItems(changes)
  }

  const toggleSelectItem = (id?: IDType) => {
    if (!id && id !== 0) {
      return
    }

    const newSelectedIdsSet = new Set(selectedIdsSet)
    const isCurrentlySelected = newSelectedIdsSet.has(id)

    if (isCurrentlySelected) {
      newSelectedIdsSet.delete(id)
      options.onItemUnselected?.(id)
    } else {
      newSelectedIdsSet.add(id)
      options.onItemSelected?.(id)
    }

    setSelectedIdsSet(newSelectedIdsSet)

    if (!options.trackChanges) {
      return
    }

    setChangedItems(c => ({ ...c, [id]: !isCurrentlySelected }))
  }

  const clearSelectedItems = () => {
    setClearSelectionTrigger(trigger => trigger + 1)
    options.onClearSelectedItems?.()
  }

  const selectedIds = useMemo(() => Array.from(selectedIdsSet), [selectedIdsSet])

  const toggleSelectItems = (ids: IDType[]) => {
    setSelectedIdsSet(prevSelectedIdsSet => {
      const newSelectedIdsSet = new Set(prevSelectedIdsSet)

      ids.forEach(id => {
        if (prevSelectedIdsSet.has(id)) {
          newSelectedIdsSet.delete(id)
          options.onItemUnselected?.(id)
        } else {
          newSelectedIdsSet.add(id)
          options.onItemSelected?.(id)
        }
      })

      if (!options.trackChanges) {
        return newSelectedIdsSet
      }

      const changes = ids.reduce((acc: Record<IDType, boolean>, id) => {
        acc[id] = !prevSelectedIdsSet.has(id)
        return acc
      }, {} as Record<IDType, boolean>)

      setChangedItems(changes)

      return newSelectedIdsSet
    })
  }

  return {
    selectedIds,
    changedItems,
    toggleSelectAll,
    selectAll,
    toggleSelectItem,
    clearSelectedItems,
    toggleSelectItems,
  }
}

export default useSelection
